import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useAuth } from '@/context/auth-context';
import { useSite } from '@/contexts/SiteContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Sparkles,
    Check,
    X,
    MessageSquare,
    MoreVertical,
    ArrowLeft,
    Loader2,
    AlertCircle,
    DollarSign,
    RefreshCw,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Download,
    Trash2,
    Filter,
    Copy,
    ExternalLink,
    Eye,
    MessageSquareText
} from 'lucide-react';

interface QuestionEvaluation {
    id: string;
    grammar_score?: number;
    language_accuracy_score?: number;
    commercial_value_score?: number;
    semantic_proximity_score?: number;
    overall_score?: number;
    evaluation_notes?: string;
    human_rating?: 'good' | 'bad' | 'fair' | null;
    feedback_text?: string;
    status: 'non_reviewed' | 'approved' | 'disregarded';
    is_negative_example: boolean;
    evaluated_at?: string;
}

interface Question {
    id: string;
    text: string;
    question_type?: string;
    sources: string[];
    search_volume: number;
    cpc: number;
    evaluation: QuestionEvaluation | null;
}

interface DebugData {
    wheelId: string;
    centerTerm: string;
    language: string;
    questions: Question[];
    summary: {
        total: number;
        evaluated: number;
        unevaluated: number;
        lowScore: number;
        approved: number;
        disregarded: number;
    };
}

interface FeedbackModalState {
    open: boolean;
    evaluationId: string | null;
    questionText: string;
    rating: 'good' | 'bad' | 'fair' | null;
    notes: string;
}

interface ProductPreview {
    id: string;
    title: string;
    brand: string | null;
    price: string | null;
}

interface ProductSelectionDialogState {
    open: boolean;
    loading: boolean;
    totalProducts: number;
    sampleProducts: ProductPreview[];
    questionsPerProduct: number;
    estimatedCost: {
        perProduct: number;
        for10: string;
        for50: string;
        for100: string;
        forAll: string;
    } | null;
    selectedProductIds: Set<string>;
    productLimit: number;
}

export default function QuestionWheelDebug() {
    const { wheelId, sectorId: pathSectorId } = useParams<{ wheelId: string; sectorId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { selectedSite } = useSite();

    // Check if this is a sector wheel - either from path param or query param (for backwards compatibility)
    const searchParams = new URLSearchParams(location.search);
    const querySectorId = searchParams.get('sector');
    const sectorId = pathSectorId || querySectorId;
    const isSectorWheel = !!sectorId;

    const [data, setData] = useState<DebugData | null>(null);
    const [loading, setLoading] = useState(true);
    const [evaluating, setEvaluating] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [copyingToSite, setCopyingToSite] = useState(false);
    const [transposingToProducts, setTransposingToProducts] = useState(false);
    const [existingSiteWheelId, setExistingSiteWheelId] = useState<string | null>(null);
    const [checkingExistingWheel, setCheckingExistingWheel] = useState(false);
    const [enrichmentStatus, setEnrichmentStatus] = useState<{ enriched: number; total: number; percentage: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [feedbackModal, setFeedbackModal] = useState<FeedbackModalState>({
        open: false,
        evaluationId: null,
        questionText: '',
        rating: null,
        notes: ''
    });
    const [sortField, setSortField] = useState<string | null>('opportunity');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [semanticRange, setSemanticRange] = useState<[number, number]>([0, 100]);
    const [commercialRange, setCommercialRange] = useState<[number, number]>([0, 100]);
    const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
    const [bulkActing, setBulkActing] = useState(false);
    const [questionTypeFilter, setQuestionTypeFilter] = useState<string | null>(null);
    const [productSelectionDialog, setProductSelectionDialog] = useState<ProductSelectionDialogState>({
        open: false,
        loading: false,
        totalProducts: 0,
        sampleProducts: [],
        questionsPerProduct: 0,
        estimatedCost: null,
        selectedProductIds: new Set(),
        productLimit: 10
    });

    // Load debug data
    useEffect(() => {
        if (wheelId) {
            loadDebugData();
        }
    }, [wheelId, sectorId]);

    // Check if this sector wheel has been copied to the selected site
    useEffect(() => {
        async function checkExistingSiteWheel() {
            if (!isSectorWheel || !sectorId || !wheelId || !selectedSite?.id) {
                setExistingSiteWheelId(null);
                return;
            }

            try {
                setCheckingExistingWheel(true);
                const response = await authFetch(`/sectors/${sectorId}/question-wheels/${wheelId}/check-site-copy?siteId=${selectedSite.id}`);

                if (!response.ok) {
                    console.warn('[QuestionWheelDebug] check-site-copy returned non-ok response:', response.status);
                    setExistingSiteWheelId(null);
                    return;
                }

                const result = await response.json();
                console.log('[QuestionWheelDebug] check-site-copy result:', result);

                // Ensure we have a valid UUID, not "undefined" string
                const existingId = result.existingWheelId;
                if (existingId && typeof existingId === 'string' && existingId !== 'undefined' && existingId !== 'null') {
                    setExistingSiteWheelId(existingId);
                } else {
                    setExistingSiteWheelId(null);
                }
            } catch (err) {
                console.error('Error checking existing site wheel:', err);
                setExistingSiteWheelId(null);
            } finally {
                setCheckingExistingWheel(false);
            }
        }

        checkExistingSiteWheel();
    }, [isSectorWheel, sectorId, wheelId, selectedSite?.id]);

    async function loadDebugData() {
        try {
            setLoading(true);
            setError(null);

            // Use sector-specific endpoint if this is a sector wheel
            const endpoint = isSectorWheel && sectorId
                ? `/sectors/${sectorId}/question-wheels/${wheelId}/debug`
                : `/question-wheels/${wheelId}/debug`;

            console.log('[QuestionWheelDebug] Fetching from endpoint:', endpoint);
            const response = await authFetch(endpoint);
            const jsonData = await response.json();
            setData(jsonData);
        } catch (err: any) {
            console.error('Error loading debug data:', err);
            setError(err.message || 'Failed to load debug data');
        } finally {
            setLoading(false);
        }
    }

    async function handleBatchEvaluate() {
        try {
            setEvaluating(true);

            // Use sector-specific endpoint if this is a sector wheel
            const endpoint = isSectorWheel && sectorId
                ? `/sectors/${sectorId}/question-wheels/${wheelId}/evaluate-batch`
                : `/question-wheels/${wheelId}/evaluate-batch`;

            console.log('[QuestionWheelDebug] Batch evaluating via endpoint:', endpoint);
            const response = await authFetch(endpoint, {
                method: 'POST'
            });
            const result = await response.json();

            // Show success message with progress
            if (result.remaining > 0) {
                alert(`✓ Evaluation started!\n\n${result.remaining} questions queued for evaluation.\n\nClick "Refresh" to see progress. Evaluation happens in the background (10 questions per batch).`);
            } else {
                alert('All questions are already evaluated!');
            }

            // Reload data to show current state
            await loadDebugData();
        } catch (err: any) {
            console.error('Error batch evaluating:', err);
            alert(`Evaluation failed: ${err.message}`);
        } finally {
            setEvaluating(false);
        }
    }

    function openFeedbackModal(question: Question) {
        if (!question.evaluation) return;

        setFeedbackModal({
            open: true,
            evaluationId: question.evaluation.id,
            questionText: question.text,
            rating: question.evaluation.human_rating || null,
            notes: question.evaluation.feedback_text || ''
        });
    }

    async function submitFeedback() {
        if (!feedbackModal.evaluationId) return;

        try {
            await authFetch(`/question-evaluations/${feedbackModal.evaluationId}/feedback`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    human_rating: feedbackModal.rating,
                    feedback_text: feedbackModal.notes
                })
            });

            setFeedbackModal({ open: false, evaluationId: null, questionText: '', rating: null, notes: '' });
            await loadDebugData();
        } catch (err: any) {
            console.error('Error submitting feedback:', err);
            alert(`Failed to submit feedback: ${err.message}`);
        }
    }

    async function handleApprove(evaluationId: string) {
        try {
            await authFetch(`/question-evaluations/${evaluationId}/approve`, {
                method: 'POST'
            });
            await loadDebugData();
        } catch (err: any) {
            console.error('Error approving:', err);
            alert(`Failed to approve: ${err.message}`);
        }
    }

    async function handleDisregard(evaluationId: string) {
        try {
            await authFetch(`/question-evaluations/${evaluationId}/disregard`, {
                method: 'POST'
            });
            await loadDebugData();
        } catch (err: any) {
            console.error('Error disregarding:', err);
            alert(`Failed to disregard: ${err.message}`);
        }
    }

    function toggleQuestion(questionId: string) {
        setSelectedQuestions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(questionId)) {
                newSet.delete(questionId);
            } else {
                newSet.add(questionId);
            }
            return newSet;
        });
    }

    function toggleSelectAll(questions: Question[]) {
        const questionIds = questions.map(q => q.id);
        const allSelected = questionIds.every(id => selectedQuestions.has(id));

        if (allSelected) {
            // Deselect all
            setSelectedQuestions(prev => {
                const newSet = new Set(prev);
                questionIds.forEach(id => newSet.delete(id));
                return newSet;
            });
        } else {
            // Select all
            setSelectedQuestions(prev => {
                const newSet = new Set(prev);
                questionIds.forEach(id => newSet.add(id));
                return newSet;
            });
        }
    }

    async function handleBulkApprove() {
        if (selectedQuestions.size === 0) {
            alert('No questions selected');
            return;
        }

        const selectedQuestionsArray = data?.questions.filter(q => selectedQuestions.has(q.id)) || [];
        const evaluationIds = selectedQuestionsArray
            .filter(q => q.evaluation && q.evaluation.status !== 'approved')
            .map(q => q.evaluation!.id);

        if (evaluationIds.length === 0) {
            alert('No questions to approve (all selected questions are already approved)');
            return;
        }

        if (!confirm(`Are you sure you want to approve ${evaluationIds.length} selected questions?`)) {
            return;
        }

        try {
            setBulkActing(true);

            await Promise.all(
                evaluationIds.map(id =>
                    authFetch(`/question-evaluations/${id}/approve`, {
                        method: 'POST'
                    })
                )
            );

            setSelectedQuestions(new Set());
            await loadDebugData();
            alert(`Successfully approved ${evaluationIds.length} questions`);
        } catch (err: any) {
            console.error('Error bulk approving:', err);
            alert(`Failed to bulk approve: ${err.message}`);
        } finally {
            setBulkActing(false);
        }
    }

    async function handleBulkDisregard() {
        if (selectedQuestions.size === 0) {
            alert('No questions selected');
            return;
        }

        const selectedQuestionsArray = data?.questions.filter(q => selectedQuestions.has(q.id)) || [];
        const evaluationIds = selectedQuestionsArray
            .filter(q => q.evaluation && q.evaluation.status !== 'disregarded')
            .map(q => q.evaluation!.id);

        if (evaluationIds.length === 0) {
            alert('No questions to disregard (all selected questions are already disregarded)');
            return;
        }

        if (!confirm(`Are you sure you want to disregard ${evaluationIds.length} selected questions?`)) {
            return;
        }

        try {
            setBulkActing(true);

            await Promise.all(
                evaluationIds.map(id =>
                    authFetch(`/question-evaluations/${id}/disregard`, {
                        method: 'POST'
                    })
                )
            );

            setSelectedQuestions(new Set());
            await loadDebugData();
            alert(`Successfully disregarded ${evaluationIds.length} questions`);
        } catch (err: any) {
            console.error('Error bulk disregarding:', err);
            alert(`Failed to bulk disregard: ${err.message}`);
        } finally {
            setBulkActing(false);
        }
    }

    async function handleBulkDelete() {
        if (selectedQuestions.size === 0) {
            alert('No questions selected');
            return;
        }

        const questionCount = selectedQuestions.size;

        if (!confirm(`⚠️ WARNING: This will permanently delete ${questionCount} question(s).\n\nThis action cannot be undone. Are you sure you want to continue?`)) {
            return;
        }

        try {
            setBulkActing(true);

            // Use sector-specific endpoint if this is a sector wheel
            const endpoint = isSectorWheel && sectorId
                ? `/sectors/${sectorId}/question-wheels/${wheelId}/bulk-delete-questions`
                : `/question-wheels/${wheelId}/bulk-delete-questions`;

            const response = await authFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    questionIds: Array.from(selectedQuestions)
                })
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.error || 'Failed to delete questions');
            }

            setSelectedQuestions(new Set());
            await loadDebugData();
            alert(`✓ Successfully deleted ${result.deleted} question(s)`);
        } catch (err: any) {
            console.error('Error bulk deleting questions:', err);
            alert(`Failed to delete questions: ${err.message}`);
        } finally {
            setBulkActing(false);
        }
    }

    async function handleEnrich() {
        try {
            setEnriching(true);

            // Use sector-specific endpoint if this is a sector wheel
            // Use new keyword-based enrichment endpoint
            const endpoint = isSectorWheel && sectorId
                ? `/sectors/${sectorId}/question-wheels/${wheelId}/enrich-with-keywords`
                : `/question-wheels/${wheelId}/enrich`;

            console.log('[QuestionWheelDebug] Enriching via endpoint:', endpoint);
            const response = await authFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}) // Empty body for all questions
            });
            const result = await response.json();

            // Check if this was async enrichment (large question set)
            if (result.async) {
                alert(
                    `✓ ${result.message}\n\n` +
                    `The enrichment is running in the background and will take several minutes.\n\n` +
                    `You can refresh this page periodically to see progress.`
                );
            } else {
                // Synchronous enrichment completed
                setEnrichmentStatus({
                    enriched: result.enriched || 0,
                    total: result.total || 0,
                    percentage: result.enrichment_rate || 0
                });
            }

            // Reload data to show any updated search volume and CPC
            await loadDebugData();
        } catch (err: any) {
            console.error('Error enriching:', err);
            alert(`Enrichment failed: ${err.message}`);
        } finally {
            setEnriching(false);
        }
    }

    async function handleCopyToSite() {
        if (!isSectorWheel || !sectorId || !wheelId || !selectedSite?.id) {
            alert('Please select a site first to copy this wheel to.');
            return;
        }

        const confirmMessage = `Copy this sector question wheel to "${selectedSite.name || selectedSite.domain}"?\n\nThis will create a site-specific version with all ${data?.summary?.total || 0} questions.`;
        if (!confirm(confirmMessage)) {
            return;
        }

        try {
            setCopyingToSite(true);
            const response = await authFetch(`/sectors/${sectorId}/question-wheels/${wheelId}/copy-to-site`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ siteId: selectedSite.id })
            });

            const result = await response.json();

            if (!response.ok) {
                if (response.status === 409) {
                    alert(`This wheel has already been copied to this site.\n\nYou can find it in the site's question wheels.`);
                } else {
                    throw new Error(result.error || 'Failed to copy wheel');
                }
                return;
            }

            // Navigate directly to the new site wheel for the customer to work on
            navigate(`/question-wheels/${result.newWheelId}/debug`);
        } catch (err: any) {
            console.error('Error copying wheel to site:', err);
            alert(`Failed to copy wheel: ${err.message}`);
        } finally {
            setCopyingToSite(false);
        }
    }

    async function handleTransposeToProducts() {
        if (!wheelId || !selectedSite?.id) {
            alert('Please select a site first.');
            return;
        }

        const category = data?.centerTerm || 'Unknown';

        // Open dialog and fetch preview
        setProductSelectionDialog(prev => ({ ...prev, open: true, loading: true }));

        try {
            const response = await authFetch(
                `/api/answer-generation/transpose-to-products/preview?wheelId=${wheelId}&siteId=${selectedSite.id}&category=${encodeURIComponent(category)}`
            );
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to load products');
            }

            setProductSelectionDialog(prev => ({
                ...prev,
                loading: false,
                totalProducts: result.totalProducts,
                sampleProducts: result.sampleProducts,
                questionsPerProduct: result.questionsPerProduct,
                estimatedCost: result.estimatedCost,
                selectedProductIds: new Set(),
                productLimit: Math.min(10, result.totalProducts)
            }));
        } catch (err: any) {
            console.error('Error loading product preview:', err);
            alert(`Failed to load products: ${err.message}`);
            setProductSelectionDialog(prev => ({ ...prev, open: false, loading: false }));
        }
    }

    async function executeProductTranspose() {
        if (!wheelId || !selectedSite?.id) return;

        const category = data?.centerTerm || 'Unknown';
        const { selectedProductIds, productLimit } = productSelectionDialog;

        setProductSelectionDialog(prev => ({ ...prev, open: false }));
        setTransposingToProducts(true);

        try {
            // Start the job
            const response = await authFetch(`/api/answer-generation/transpose-to-products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    wheelId,
                    siteId: selectedSite.id,
                    category,
                    limit: productLimit,
                    productIds: selectedProductIds.size > 0 ? Array.from(selectedProductIds) : []
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to start product question generation');
            }

            // Job started - now poll for status
            const jobId = result.jobId;
            if (!jobId) {
                throw new Error('No job ID returned');
            }

            // Poll for job completion
            let completed = false;
            let lastProgress = 0;
            while (!completed) {
                await new Promise(resolve => setTimeout(resolve, 2000)); // Poll every 2 seconds

                const statusResponse = await authFetch(`/api/jobs/${jobId}`);
                const statusResult = await statusResponse.json();

                if (statusResult.status === 'completed') {
                    completed = true;
                    const jobResult = statusResult.result || {};
                    alert(`Success!\n\nGenerated ${jobResult.questionsGenerated || 0} product-specific questions for ${jobResult.productsProcessed || 0} products.\n\nNew wheel created: "${jobResult.wheelName || 'Product Questions'}"`);
                } else if (statusResult.status === 'failed') {
                    throw new Error(statusResult.error || 'Job failed');
                } else {
                    // Still processing - show progress
                    const progress = statusResult.progress || {};
                    if (progress.percent !== lastProgress) {
                        lastProgress = progress.percent;
                        console.log(`[Job ${jobId}] ${progress.percent}% - ${progress.message}`);
                    }
                }
            }
        } catch (err: any) {
            console.error('Error generating product questions:', err);
            alert(`Failed to generate product questions: ${err.message}`);
        } finally {
            setTransposingToProducts(false);
        }
    }

    function toggleProductSelection(productId: string) {
        setProductSelectionDialog(prev => {
            const newSelected = new Set(prev.selectedProductIds);
            if (newSelected.has(productId)) {
                newSelected.delete(productId);
            } else {
                newSelected.add(productId);
            }
            return { ...prev, selectedProductIds: newSelected };
        });
    }

    function getScoreBadgeVariant(score: number) {
        if (score >= 80) return 'default'; // Green
        if (score >= 60) return 'secondary'; // Yellow/Warning
        return 'destructive'; // Red
    }

    // Calculate source counts
    function getSourceCounts(questions: Question[]): Record<string, number> {
        const counts: Record<string, number> = {};
        questions.forEach(q => {
            if (q.sources && Array.isArray(q.sources)) {
                q.sources.forEach(source => {
                    counts[source] = (counts[source] || 0) + 1;
                });
            }
        });
        return counts;
    }

    // Get unique sources from all questions
    function getAvailableSources(questions: Question[]): string[] {
        const sourcesSet = new Set<string>();
        questions.forEach(q => {
            if (q.sources && Array.isArray(q.sources)) {
                q.sources.forEach(source => sourcesSet.add(source));
            }
        });
        return Array.from(sourcesSet).sort();
    }

    function filterQuestions(questions: Question[], filter: string): Question[] {
        let filtered = questions;

        // Check if it's a source filter (starts with 'source:')
        if (filter.startsWith('source:')) {
            const sourceType = filter.replace('source:', '');
            filtered = filtered.filter(q => q.sources && q.sources.includes(sourceType));
        } else {
            // Regular evaluation-based filters
            switch (filter) {
                case 'unevaluated':
                    filtered = filtered.filter(q => !q.evaluation);
                    break;
                case 'low-score':
                    filtered = filtered.filter(q => q.evaluation && q.evaluation.overall_score && q.evaluation.overall_score < 60);
                    break;
                case 'approved':
                    filtered = filtered.filter(q => q.evaluation && q.evaluation.status === 'approved');
                    break;
                case 'disregarded':
                    filtered = filtered.filter(q => q.evaluation && q.evaluation.status === 'disregarded');
                    break;
            }
        }

        // Apply question type filter if set
        if (questionTypeFilter) {
            filtered = filtered.filter(q => q.question_type === questionTypeFilter);
        }

        return filtered;
    }

    function applyRangeFilters(questions: Question[]): Question[] {
        return questions.filter(q => {
            // Apply semantic proximity range filter
            const semanticScore = q.evaluation?.semantic_proximity_score || 0;
            if (semanticScore < semanticRange[0] || semanticScore > semanticRange[1]) {
                return false;
            }

            // Apply commercial value range filter
            const commercialScore = q.evaluation?.commercial_value_score || 0;
            if (commercialScore < commercialRange[0] || commercialScore > commercialRange[1]) {
                return false;
            }

            return true;
        });
    }

    // Get human-friendly source label
    function getSourceLabel(source: string): string {
        const labels: Record<string, string> = {
            'paa': 'PAA (People Also Ask)',
            'buyer_persona': 'Buyer Persona',
            'ai_generation': 'AI Generated',
            'google_ads': 'Google Ads',
            'google_ads_semantic': 'Google Ads (Semantic)',
        };
        return labels[source] || source;
    }

    // Get unique question types from all questions
    function getAvailableQuestionTypes(questions: Question[]): string[] {
        const typesSet = new Set<string>();
        questions.forEach(q => {
            if (q.question_type) {
                typesSet.add(q.question_type);
            }
        });
        return Array.from(typesSet).sort();
    }

    // Get human-friendly question type label
    function getQuestionTypeLabel(questionType: string): string {
        const labels: Record<string, string> = {
            'PC-PA': 'Product Category - Product Attribute',
            'PC-PB': 'Product Category - Perceived Benefit',
            'PC-BP': 'Product Category - Buyer Persona',
            'PC-PA-PROD': 'Product-Specific Attribute',
            'PC-PB-PROD': 'Product-Specific Benefit',
            'PC-BP-PROD': 'Product-Specific Use-Case',
            'PB-RANK': 'Benefit Ranking',
            'BP-RANK': 'Buyer Persona Ranking',
            'BP-PC-PB': 'Buyer Persona - Category Benefit',
            'PC-PB-CMP': 'Category Benefit Comparison',
            'PC-PB-CMP-NPROD': 'Category Benefit Comparison (N-Product)',
            'PC-BP-PB-CMP': 'Category Buyer Persona Benefit Comparison',
            'PC-BP-PB-CMP-NPROD': 'Category Buyer Persona Benefit Comparison (N-Product)',
            'PC-TR': 'Product Category - Transaction',
            'category': 'Category Questions',
        };
        return labels[questionType] || questionType;
    }

    // Handle column header click for sorting
    function handleSort(field: string) {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to descending
            setSortField(field);
            setSortDirection('desc');
        }
    }

    // Sort questions based on current sort field and direction
    function sortQuestions(questions: Question[]): Question[] {
        if (!sortField) return questions;

        return [...questions].sort((a, b) => {
            let aVal: any;
            let bVal: any;

            switch (sortField) {
                case 'question':
                    aVal = a.text.toLowerCase();
                    bVal = b.text.toLowerCase();
                    break;
                case 'volume':
                    aVal = a.search_volume || 0;
                    bVal = b.search_volume || 0;
                    break;
                case 'cpc':
                    aVal = a.cpc || 0;
                    bVal = b.cpc || 0;
                    break;
                case 'opportunity':
                    aVal = (a.search_volume || 0) * (a.cpc || 0);
                    bVal = (b.search_volume || 0) * (b.cpc || 0);
                    break;
                case 'grammar':
                    aVal = a.evaluation?.grammar_score || 0;
                    bVal = b.evaluation?.grammar_score || 0;
                    break;
                case 'language':
                    aVal = a.evaluation?.language_accuracy_score || 0;
                    bVal = b.evaluation?.language_accuracy_score || 0;
                    break;
                case 'commercial':
                    aVal = a.evaluation?.commercial_value_score || 0;
                    bVal = b.evaluation?.commercial_value_score || 0;
                    break;
                case 'semantic':
                    aVal = a.evaluation?.semantic_proximity_score || 0;
                    bVal = b.evaluation?.semantic_proximity_score || 0;
                    break;
                case 'overall':
                    aVal = a.evaluation?.overall_score || 0;
                    bVal = b.evaluation?.overall_score || 0;
                    break;
                case 'rating':
                    aVal = a.evaluation?.human_rating || '';
                    bVal = b.evaluation?.human_rating || '';
                    break;
                case 'status':
                    aVal = a.evaluation?.status || '';
                    bVal = b.evaluation?.status || '';
                    break;
                default:
                    return 0;
            }

            // Compare values
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }

    // Render sort icon for column header
    function renderSortIcon(field: string) {
        if (sortField !== field) {
            return <ArrowUpDown className="w-4 h-4 ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc'
            ? <ArrowUp className="w-4 h-4 ml-1 text-primary" />
            : <ArrowDown className="w-4 h-4 ml-1 text-primary" />;
    }

    // Download CSV function
    function downloadCSV() {
        if (!data) return;

        // CSV Headers
        const headers = [
            'Question',
            'Sources',
            'Search Volume',
            'CPC',
            'Opportunity Size',
            'Grammar Score',
            'Language Score',
            'Commercial Score',
            'Semantic Score',
            'Overall Score',
            'Human Rating',
            'Status',
            'Evaluated At'
        ];

        // Build CSV rows
        const rows = data.questions.map(q => {
            const eval_data = q.evaluation;
            const opportunitySize = (q.search_volume || 0) * (q.cpc || 0);

            return [
                `"${q.text.replace(/"/g, '""')}"`, // Escape quotes in question text
                `"${q.sources.join(', ')}"`,
                q.search_volume || 0,
                q.cpc?.toFixed(2) || '0.00',
                opportunitySize.toFixed(2),
                eval_data?.grammar_score || '',
                eval_data?.language_accuracy_score || '',
                eval_data?.commercial_value_score || '',
                eval_data?.semantic_proximity_score || '',
                eval_data?.overall_score || '',
                eval_data?.human_rating || '',
                eval_data?.status || 'non_reviewed',
                eval_data?.evaluated_at ? new Date(eval_data.evaluated_at).toISOString() : ''
            ].join(',');
        });

        // Combine headers and rows
        const csvContent = [headers.join(','), ...rows].join('\n');

        // Add UTF-8 BOM to ensure proper encoding in Excel and other programs
        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvContent;

        // Create blob and download
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `question-wheel-${wheelId}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !data || !data.summary) {
        return (
            <div className="p-8">
                <div className="flex items-center gap-2 text-red-600 mb-4">
                    <AlertCircle className="w-5 h-5" />
                    <h2 className="text-xl font-semibold">Error Loading Debug Data</h2>
                </div>
                <p className="text-gray-600 mb-4">{error || 'No data available or invalid data format'}</p>
                <Button onClick={() => navigate(-1)} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Go Back
                </Button>
            </div>
        );
    }

    // Determine if user can evaluate (super_admin for sector, site owner/content manager for site)
    const canEvaluate = isSectorWheel
        ? user?.role === 'super_admin'
        : true; // Site owners can always evaluate their own wheels

    // Generate page title based on wheel type and user permissions
    const getPageTitle = () => {
        if (isSectorWheel) {
            return canEvaluate ? 'Sector Question Wheel Evaluation' : 'Sector Question Wheel Viewer';
        }
        return 'Site Question Wheel Evaluation';
    };

    return (
        <div className="p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Button onClick={() => navigate(-1)} variant="ghost" size="sm">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        {isSectorWheel && (
                            <Badge variant="secondary">Sector Level</Badge>
                        )}
                    </div>
                    <h1 className="text-3xl font-bold">{getPageTitle()}</h1>
                    <p className="text-gray-600">Center Term: {data.centerTerm} | Language: {(data.language || 'en').toUpperCase()}</p>
                    {enrichmentStatus && (
                        <div className="mt-2 flex items-center gap-2">
                            <Badge variant="outline" className="text-green-600">
                                Enriched: {enrichmentStatus.enriched}/{enrichmentStatus.total} questions ({enrichmentStatus.percentage}%)
                            </Badge>
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button onClick={downloadCSV} variant="outline" disabled={!data}>
                        <Download className="w-4 h-4 mr-2" />
                        Download CSV
                    </Button>
                    {/* Only show Evaluate All if user can evaluate */}
                    {canEvaluate && (
                        <Button onClick={handleBatchEvaluate} disabled={evaluating || loading}>
                            {evaluating ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Evaluating...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Evaluate All
                                </>
                            )}
                        </Button>
                    )}
                    <Button onClick={loadDebugData} disabled={loading} variant="outline">
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Refreshing...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh
                            </>
                        )}
                    </Button>
                    {/* Only show Enrich if user can evaluate */}
                    {canEvaluate && user?.role === 'super_admin' && (
                        <Button onClick={handleEnrich} disabled={enriching} variant="secondary">
                            {enriching ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Enriching...
                                </>
                            ) : (
                                <>
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Enrich with Google Ads
                                </>
                            )}
                        </Button>
                    )}
                    {/* Copy to Site OR Site Wheel Available button - only for sector wheels */}
                    {isSectorWheel && selectedSite && (
                        existingSiteWheelId && existingSiteWheelId !== 'undefined' ? (
                            <Button
                                onClick={() => {
                                    if (existingSiteWheelId && existingSiteWheelId !== 'undefined') {
                                        navigate(`/question-wheels/${existingSiteWheelId}/debug`);
                                    }
                                }}
                                variant="default"
                                className="bg-green-600 hover:bg-green-700"
                            >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Site Question Wheel Available
                            </Button>
                        ) : (
                            <Button onClick={handleCopyToSite} disabled={copyingToSite || loading || checkingExistingWheel} variant="outline">
                                {copyingToSite ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Copying...
                                    </>
                                ) : checkingExistingWheel ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copy to {selectedSite.name || selectedSite.domain}
                                    </>
                                )}
                            </Button>
                        )
                    )}
                    {/* Answer with your catalog - only for site wheels */}
                    {!isSectorWheel && (
                        <Button
                            onClick={() => navigate('/answer-generation', {
                                state: {
                                    fromQuestionWheel: true,
                                    wheelId: wheelId
                                }
                            })}
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                        >
                            <MessageSquareText className="w-4 h-4 mr-2" />
                            Answer with your catalog
                        </Button>
                    )}
                    {/* Generate Product Questions - for site wheels with products */}
                    {!isSectorWheel && selectedSite && (
                        <Button
                            onClick={handleTransposeToProducts}
                            disabled={transposingToProducts || loading}
                            variant="outline"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                        >
                            {transposingToProducts ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Generate Product Questions
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="all" className="space-y-4">
                <TabsList className="flex flex-wrap h-auto">
                    <TabsTrigger value="all">All Questions ({data?.summary?.total || 0})</TabsTrigger>
                    <TabsTrigger value="unevaluated">Need Evaluation ({data?.summary?.unevaluated || 0})</TabsTrigger>
                    <TabsTrigger value="low-score">Low Score ({data?.summary?.lowScore || 0})</TabsTrigger>
                    <TabsTrigger value="approved">Approved ({data?.summary?.approved || 0})</TabsTrigger>
                    <TabsTrigger value="disregarded">Disregarded ({data?.summary?.disregarded || 0})</TabsTrigger>

                    {/* Source-based tabs */}
                    {getAvailableSources(data.questions).map(source => {
                        const sourceCounts = getSourceCounts(data.questions);
                        return (
                            <TabsTrigger key={source} value={`source:${source}`}>
                                {getSourceLabel(source)} ({sourceCounts[source] || 0})
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {/* Evaluation-based tabs */}
                {(['all', 'unevaluated', 'low-score', 'approved', 'disregarded'] as const).map(tab => (
                    <TabsContent key={tab} value={tab}>
                        <Card>
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle>
                                            {tab === 'all' && 'All Questions'}
                                            {tab === 'unevaluated' && 'Questions Needing Evaluation'}
                                            {tab === 'low-score' && 'Low Quality Questions (Score < 60)'}
                                            {tab === 'approved' && 'Approved Questions'}
                                            {tab === 'disregarded' && 'Disregarded Questions'}
                                        </CardTitle>
                                        <CardDescription>
                                            {tab === 'unevaluated' && 'These questions have not been evaluated yet'}
                                            {tab === 'low-score' && 'These questions scored poorly and may need review'}
                                            {tab === 'approved' && 'These questions have been approved for use'}
                                            {tab === 'disregarded' && 'These questions are marked as negative examples for fine-tuning'}
                                        </CardDescription>
                                    </div>
                                    {selectedQuestions.size > 0 && (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={handleBulkApprove}
                                                variant="default"
                                                size="sm"
                                                disabled={bulkActing}
                                            >
                                                {bulkActing ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Check className="w-4 h-4 mr-2" />
                                                        Approve Selected ({selectedQuestions.size})
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                onClick={handleBulkDisregard}
                                                variant="destructive"
                                                size="sm"
                                                disabled={bulkActing}
                                            >
                                                {bulkActing ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <X className="w-4 h-4 mr-2" />
                                                        Disregard Selected ({selectedQuestions.size})
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                onClick={handleBulkDelete}
                                                variant="outline"
                                                size="sm"
                                                disabled={bulkActing}
                                                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                                            >
                                                {bulkActing ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Delete Selected ({selectedQuestions.size})
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Filters */}
                                <div className="mt-4 grid grid-cols-3 gap-6 border-t pt-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium flex items-center gap-2">
                                                <Filter className="w-4 h-4" />
                                                Question Type
                                            </Label>
                                            {questionTypeFilter && (
                                                <Button
                                                    onClick={() => setQuestionTypeFilter(null)}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs"
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                        <Select
                                            value={questionTypeFilter || 'all'}
                                            onValueChange={(value) => setQuestionTypeFilter(value === 'all' ? null : value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="All question types" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Types</SelectItem>
                                                {getAvailableQuestionTypes(data.questions).map(qType => (
                                                    <SelectItem key={qType} value={qType}>
                                                        {getQuestionTypeLabel(qType)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            {questionTypeFilter
                                                ? `Showing: ${getQuestionTypeLabel(questionTypeFilter)}`
                                                : 'Showing all question types'}
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium">
                                                Semantic Proximity: {semanticRange[0]}-{semanticRange[1]}
                                            </Label>
                                            {(semanticRange[0] !== 0 || semanticRange[1] !== 100) && (
                                                <Button
                                                    onClick={() => setSemanticRange([0, 100])}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs"
                                                >
                                                    Reset
                                                </Button>
                                            )}
                                        </div>
                                        <Slider
                                            value={semanticRange}
                                            onValueChange={(values) => setSemanticRange(values as [number, number])}
                                            min={0}
                                            max={100}
                                            step={5}
                                            minStepsBetweenThumbs={1}
                                            className="w-full"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Show questions with semantic score between {semanticRange[0]} and {semanticRange[1]}
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium">
                                                Commercial Value: {commercialRange[0]}-{commercialRange[1]}
                                            </Label>
                                            {(commercialRange[0] !== 0 || commercialRange[1] !== 100) && (
                                                <Button
                                                    onClick={() => setCommercialRange([0, 100])}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs"
                                                >
                                                    Reset
                                                </Button>
                                            )}
                                        </div>
                                        <Slider
                                            value={commercialRange}
                                            onValueChange={(values) => setCommercialRange(values as [number, number])}
                                            min={0}
                                            max={100}
                                            step={5}
                                            minStepsBetweenThumbs={1}
                                            className="w-full"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Show questions with commercial value between {commercialRange[0]} and {commercialRange[1]}
                                        </p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">
                                                {(() => {
                                                    const filteredQuestions = sortQuestions(applyRangeFilters(filterQuestions(data.questions, tab)));
                                                    const allSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedQuestions.has(q.id));
                                                    return (
                                                        <Checkbox
                                                            checked={allSelected}
                                                            onCheckedChange={() => toggleSelectAll(filteredQuestions)}
                                                        />
                                                    );
                                                })()}
                                            </TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('question')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Question
                                                    {renderSortIcon('question')}
                                                </button>
                                            </TableHead>
                                            <TableHead>Sources</TableHead>
                                            <TableHead className="text-right">
                                                <button
                                                    onClick={() => handleSort('volume')}
                                                    className="flex items-center justify-end hover:text-primary cursor-pointer ml-auto"
                                                >
                                                    Volume
                                                    {renderSortIcon('volume')}
                                                </button>
                                            </TableHead>
                                            <TableHead className="text-right">
                                                <button
                                                    onClick={() => handleSort('cpc')}
                                                    className="flex items-center justify-end hover:text-primary cursor-pointer ml-auto"
                                                >
                                                    CPC
                                                    {renderSortIcon('cpc')}
                                                </button>
                                            </TableHead>
                                            <TableHead className="text-right">
                                                <button
                                                    onClick={() => handleSort('opportunity')}
                                                    className="flex items-center justify-end hover:text-primary cursor-pointer ml-auto"
                                                >
                                                    Opportunity Size
                                                    {renderSortIcon('opportunity')}
                                                </button>
                                            </TableHead>
                                            <TableHead>Scores</TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('overall')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Overall
                                                    {renderSortIcon('overall')}
                                                </button>
                                            </TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('rating')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Rating
                                                    {renderSortIcon('rating')}
                                                </button>
                                            </TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('status')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Status
                                                    {renderSortIcon('status')}
                                                </button>
                                            </TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sortQuestions(applyRangeFilters(filterQuestions(data.questions, tab))).map((question) => (
                                            <TableRow key={question.id}>
                                                <TableCell className="w-12">
                                                    <Checkbox
                                                        checked={selectedQuestions.has(question.id)}
                                                        onCheckedChange={() => toggleQuestion(question.id)}
                                                    />
                                                </TableCell>
                                                <TableCell className="max-w-md">
                                                    <div>{question.text}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {question.sources.map(source => (
                                                            <Badge key={source} variant="outline" className="text-xs">
                                                                {source}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">{question.search_volume?.toLocaleString() || 0}</TableCell>
                                                <TableCell className="text-right">${question.cpc?.toFixed(2) || '0.00'}</TableCell>
                                                <TableCell className="text-right font-semibold">
                                                    ${((question.search_volume || 0) * (question.cpc || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation ? (
                                                        <div className="space-y-1 min-w-[200px]">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs w-20">Grammar:</span>
                                                                <Progress value={question.evaluation.grammar_score} className="h-1 flex-1" />
                                                                <span className="text-xs w-8 text-right">{question.evaluation.grammar_score}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs w-20">Language:</span>
                                                                <Progress value={question.evaluation.language_accuracy_score} className="h-1 flex-1" />
                                                                <span className="text-xs w-8 text-right">{question.evaluation.language_accuracy_score}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs w-20">Commercial:</span>
                                                                <Progress value={question.evaluation.commercial_value_score} className="h-1 flex-1" />
                                                                <span className="text-xs w-8 text-right">{question.evaluation.commercial_value_score}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs w-20">Semantic:</span>
                                                                <Progress value={question.evaluation.semantic_proximity_score} className="h-1 flex-1" />
                                                                <span className="text-xs w-8 text-right">{question.evaluation.semantic_proximity_score}</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <Badge variant="secondary">Not Evaluated</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation?.overall_score ? (
                                                        <Badge variant={getScoreBadgeVariant(question.evaluation.overall_score)}>
                                                            {question.evaluation.overall_score}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation?.human_rating ? (
                                                        <Badge variant="outline">{question.evaluation.human_rating}</Badge>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation ? (
                                                        <Badge variant={
                                                            question.evaluation.status === 'approved' ? 'default' :
                                                            question.evaluation.status === 'disregarded' ? 'destructive' :
                                                            'secondary'
                                                        }>
                                                            {question.evaluation.status}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation ? (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm">
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => handleApprove(question.evaluation!.id)}>
                                                                    <Check className="w-4 h-4 mr-2" />
                                                                    Approve
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleDisregard(question.evaluation!.id)}>
                                                                    <X className="w-4 h-4 mr-2" />
                                                                    Disregard
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => openFeedbackModal(question)}>
                                                                    <MessageSquare className="w-4 h-4 mr-2" />
                                                                    Add Feedback
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                ))}

                {/* Source-based tabs content */}
                {getAvailableSources(data.questions).map(source => (
                    <TabsContent key={`source:${source}`} value={`source:${source}`}>
                        <Card>
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle>Questions from {getSourceLabel(source)}</CardTitle>
                                        <CardDescription>
                                            Showing all questions that include this source
                                        </CardDescription>
                                    </div>
                                    {selectedQuestions.size > 0 && (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={handleBulkApprove}
                                                variant="default"
                                                size="sm"
                                                disabled={bulkActing}
                                            >
                                                {bulkActing ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Check className="w-4 h-4 mr-2" />
                                                        Approve Selected ({selectedQuestions.size})
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                onClick={handleBulkDisregard}
                                                variant="destructive"
                                                size="sm"
                                                disabled={bulkActing}
                                            >
                                                {bulkActing ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <X className="w-4 h-4 mr-2" />
                                                        Disregard Selected ({selectedQuestions.size})
                                                    </>
                                                )}
                                            </Button>
                                            <Button
                                                onClick={handleBulkDelete}
                                                variant="outline"
                                                size="sm"
                                                disabled={bulkActing}
                                                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                                            >
                                                {bulkActing ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Deleting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Delete Selected ({selectedQuestions.size})
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Filters */}
                                <div className="mt-4 grid grid-cols-3 gap-6 border-t pt-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium flex items-center gap-2">
                                                <Filter className="w-4 h-4" />
                                                Question Type
                                            </Label>
                                            {questionTypeFilter && (
                                                <Button
                                                    onClick={() => setQuestionTypeFilter(null)}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs"
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                        <Select
                                            value={questionTypeFilter || 'all'}
                                            onValueChange={(value) => setQuestionTypeFilter(value === 'all' ? null : value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="All question types" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Types</SelectItem>
                                                {getAvailableQuestionTypes(data.questions).map(qType => (
                                                    <SelectItem key={qType} value={qType}>
                                                        {getQuestionTypeLabel(qType)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            {questionTypeFilter
                                                ? `Showing: ${getQuestionTypeLabel(questionTypeFilter)}`
                                                : 'Showing all question types'}
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium">
                                                Semantic Proximity: {semanticRange[0]}-{semanticRange[1]}
                                            </Label>
                                            {(semanticRange[0] !== 0 || semanticRange[1] !== 100) && (
                                                <Button
                                                    onClick={() => setSemanticRange([0, 100])}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs"
                                                >
                                                    Reset
                                                </Button>
                                            )}
                                        </div>
                                        <Slider
                                            value={semanticRange}
                                            onValueChange={(values) => setSemanticRange(values as [number, number])}
                                            min={0}
                                            max={100}
                                            step={5}
                                            minStepsBetweenThumbs={1}
                                            className="w-full"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Show questions with semantic score between {semanticRange[0]} and {semanticRange[1]}
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-sm font-medium">
                                                Commercial Value: {commercialRange[0]}-{commercialRange[1]}
                                            </Label>
                                            {(commercialRange[0] !== 0 || commercialRange[1] !== 100) && (
                                                <Button
                                                    onClick={() => setCommercialRange([0, 100])}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 text-xs"
                                                >
                                                    Reset
                                                </Button>
                                            )}
                                        </div>
                                        <Slider
                                            value={commercialRange}
                                            onValueChange={(values) => setCommercialRange(values as [number, number])}
                                            min={0}
                                            max={100}
                                            step={5}
                                            minStepsBetweenThumbs={1}
                                            className="w-full"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Show questions with commercial value between {commercialRange[0]} and {commercialRange[1]}
                                        </p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">
                                                {(() => {
                                                    const filteredQuestions = sortQuestions(applyRangeFilters(filterQuestions(data.questions, `source:${source}`)));
                                                    const allSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedQuestions.has(q.id));
                                                    return (
                                                        <Checkbox
                                                            checked={allSelected}
                                                            onCheckedChange={() => toggleSelectAll(filteredQuestions)}
                                                        />
                                                    );
                                                })()}
                                            </TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('question')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Question
                                                    {renderSortIcon('question')}
                                                </button>
                                            </TableHead>
                                            <TableHead>Sources</TableHead>
                                            <TableHead className="text-right">
                                                <button
                                                    onClick={() => handleSort('volume')}
                                                    className="flex items-center justify-end hover:text-primary cursor-pointer ml-auto"
                                                >
                                                    Volume
                                                    {renderSortIcon('volume')}
                                                </button>
                                            </TableHead>
                                            <TableHead className="text-right">
                                                <button
                                                    onClick={() => handleSort('cpc')}
                                                    className="flex items-center justify-end hover:text-primary cursor-pointer ml-auto"
                                                >
                                                    CPC
                                                    {renderSortIcon('cpc')}
                                                </button>
                                            </TableHead>
                                            <TableHead className="text-right">
                                                <button
                                                    onClick={() => handleSort('opportunity')}
                                                    className="flex items-center justify-end hover:text-primary cursor-pointer ml-auto"
                                                >
                                                    Opportunity Size
                                                    {renderSortIcon('opportunity')}
                                                </button>
                                            </TableHead>
                                            <TableHead>Scores</TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('overall')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Overall
                                                    {renderSortIcon('overall')}
                                                </button>
                                            </TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('rating')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Rating
                                                    {renderSortIcon('rating')}
                                                </button>
                                            </TableHead>
                                            <TableHead>
                                                <button
                                                    onClick={() => handleSort('status')}
                                                    className="flex items-center hover:text-primary cursor-pointer"
                                                >
                                                    Status
                                                    {renderSortIcon('status')}
                                                </button>
                                            </TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sortQuestions(applyRangeFilters(filterQuestions(data.questions, `source:${source}`))).map((question) => (
                                            <TableRow key={question.id}>
                                                <TableCell className="w-12">
                                                    <Checkbox
                                                        checked={selectedQuestions.has(question.id)}
                                                        onCheckedChange={() => toggleQuestion(question.id)}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium max-w-md">
                                                    {question.text}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {question.sources.map(src => (
                                                            <Badge key={src} variant="outline" className="text-xs">
                                                                {src}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {question.search_volume?.toLocaleString() || '-'}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {question.cpc ? `$${question.cpc.toFixed(2)}` : '-'}
                                                </TableCell>
                                                <TableCell className="text-right font-semibold">
                                                    {(question.search_volume && question.cpc)
                                                        ? `$${((question.search_volume || 0) * (question.cpc || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation ? (
                                                        <div className="flex flex-col gap-1 text-xs">
                                                            <div>G: {question.evaluation.grammar_score || 'N/A'}</div>
                                                            <div>L: {question.evaluation.language_accuracy_score || 'N/A'}</div>
                                                            <div>C: {question.evaluation.commercial_value_score || 'N/A'}</div>
                                                            <div>S: {question.evaluation.semantic_proximity_score || 'N/A'}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation?.overall_score && (
                                                        <Badge variant={getScoreBadgeVariant(question.evaluation.overall_score)}>
                                                            {question.evaluation.overall_score}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation?.human_rating && (
                                                        <Badge variant="outline">
                                                            {question.evaluation.human_rating}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation ? (
                                                        <Badge variant={
                                                            question.evaluation.status === 'approved' ? 'default' :
                                                            question.evaluation.status === 'disregarded' ? 'destructive' :
                                                            'secondary'
                                                        }>
                                                            {question.evaluation.status}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {question.evaluation ? (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm">
                                                                    <MoreVertical className="w-4 h-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => handleApprove(question.evaluation!.id)}>
                                                                    <Check className="w-4 h-4 mr-2" />
                                                                    Approve
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleDisregard(question.evaluation!.id)}>
                                                                    <X className="w-4 h-4 mr-2" />
                                                                    Disregard
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => openFeedbackModal(question)}>
                                                                    <MessageSquare className="w-4 h-4 mr-2" />
                                                                    Add Feedback
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                ))}
            </Tabs>

            {/* Feedback Modal */}
            <Dialog open={feedbackModal.open} onOpenChange={(open) => {
                if (!open) {
                    setFeedbackModal({ open: false, evaluationId: null, questionText: '', rating: null, notes: '' });
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Provide Feedback</DialogTitle>
                        <DialogDescription className="line-clamp-2">{feedbackModal.questionText}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Rating</Label>
                            <RadioGroup value={feedbackModal.rating || ''} onValueChange={(value) =>
                                setFeedbackModal(prev => ({ ...prev, rating: value as 'good' | 'bad' | 'fair' }))
                            }>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="good" id="good" />
                                    <Label htmlFor="good">Good</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="fair" id="fair" />
                                    <Label htmlFor="fair">Fair</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="bad" id="bad" />
                                    <Label htmlFor="bad">Bad</Label>
                                </div>
                            </RadioGroup>
                        </div>
                        <div>
                            <Label>Feedback Notes</Label>
                            <Textarea
                                value={feedbackModal.notes}
                                onChange={(e) => setFeedbackModal(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Optional notes about this question..."
                                rows={4}
                            />
                        </div>
                        <Button onClick={submitFeedback} className="w-full">
                            Submit Feedback
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Product Selection Dialog */}
            <Dialog open={productSelectionDialog.open} onOpenChange={(open) => {
                if (!open) {
                    setProductSelectionDialog(prev => ({ ...prev, open: false }));
                }
            }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Generate Product Questions</DialogTitle>
                        <DialogDescription>
                            Select which products to generate questions for. Each product will have questions reformulated from the category wheel.
                        </DialogDescription>
                    </DialogHeader>

                    {productSelectionDialog.loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin" />
                            <span className="ml-2">Loading products...</span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Summary */}
                            <Card>
                                <CardContent className="pt-4">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-muted-foreground">Total matching products:</span>
                                            <span className="ml-2 font-semibold">{productSelectionDialog.totalProducts.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Questions per product:</span>
                                            <span className="ml-2 font-semibold">~{productSelectionDialog.questionsPerProduct}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Cost Estimate */}
                            {productSelectionDialog.estimatedCost && (
                                <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
                                    <CardContent className="pt-4">
                                        <div className="flex items-start gap-2">
                                            <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-amber-800 dark:text-amber-200">Estimated API Cost</p>
                                                <div className="text-sm text-amber-700 dark:text-amber-300 mt-1 space-y-1">
                                                    <p>10 products: ~${productSelectionDialog.estimatedCost.for10}</p>
                                                    <p>50 products: ~${productSelectionDialog.estimatedCost.for50}</p>
                                                    <p>100 products: ~${productSelectionDialog.estimatedCost.for100}</p>
                                                    <p className="font-medium">All {productSelectionDialog.totalProducts} products: ~${productSelectionDialog.estimatedCost.forAll}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Selection Mode */}
                            <div className="space-y-3">
                                <Label>How many products to process?</Label>
                                <RadioGroup
                                    value={productSelectionDialog.selectedProductIds.size > 0 ? 'specific' : 'limit'}
                                    onValueChange={(value) => {
                                        if (value === 'limit') {
                                            setProductSelectionDialog(prev => ({ ...prev, selectedProductIds: new Set() }));
                                        }
                                    }}
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="limit" id="limit-mode" />
                                        <Label htmlFor="limit-mode" className="flex items-center gap-2">
                                            First
                                            <Select
                                                value={productSelectionDialog.productLimit.toString()}
                                                onValueChange={(val) => setProductSelectionDialog(prev => ({
                                                    ...prev,
                                                    productLimit: parseInt(val),
                                                    selectedProductIds: new Set()
                                                }))}
                                            >
                                                <SelectTrigger className="w-20 h-8">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="5">5</SelectItem>
                                                    <SelectItem value="10">10</SelectItem>
                                                    <SelectItem value="20">20</SelectItem>
                                                    <SelectItem value="50">50</SelectItem>
                                                    <SelectItem value="100">100</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            products
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="specific" id="specific-mode" />
                                        <Label htmlFor="specific-mode">Select specific products below</Label>
                                    </div>
                                </RadioGroup>
                            </div>

                            {/* Product List for Selection */}
                            <div className="border rounded-md max-h-60 overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-10"></TableHead>
                                            <TableHead>Product</TableHead>
                                            <TableHead>Brand</TableHead>
                                            <TableHead className="text-right">Price</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {productSelectionDialog.sampleProducts.map((product) => (
                                            <TableRow key={product.id} className="cursor-pointer" onClick={() => toggleProductSelection(product.id)}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={productSelectionDialog.selectedProductIds.has(product.id)}
                                                        onCheckedChange={() => toggleProductSelection(product.id)}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium max-w-xs truncate">{product.title}</TableCell>
                                                <TableCell>{product.brand || '-'}</TableCell>
                                                <TableCell className="text-right">{product.price || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            {productSelectionDialog.totalProducts > 50 && (
                                <p className="text-xs text-muted-foreground">
                                    Showing first 50 of {productSelectionDialog.totalProducts.toLocaleString()} products
                                </p>
                            )}

                            {/* Selected count */}
                            {productSelectionDialog.selectedProductIds.size > 0 && (
                                <p className="text-sm font-medium text-blue-600">
                                    {productSelectionDialog.selectedProductIds.size} products selected
                                </p>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setProductSelectionDialog(prev => ({ ...prev, open: false }))}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={executeProductTranspose}
                                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                                >
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Generate Questions
                                    {productSelectionDialog.selectedProductIds.size > 0
                                        ? ` (${productSelectionDialog.selectedProductIds.size})`
                                        : ` (${productSelectionDialog.productLimit})`
                                    }
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
